import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';

/**
 * Row shape returned by `loadJsonObject?action_code=bma33d`.
 * Mirrors the columns rendered in `pdf-upload.html` L1935-1945.
 */
export interface UploadedPdf {
  FILE_NAME: string;
  COMMENTS: string;
  USER_NAME: string;
  FORMATTED_CREATE_DATE: string;
  /** Client-side only, used to gate the per-row action spinner. Not sent by backend. */
  loadingPdf?: boolean;
}

/** Row shape returned by `loadJsonObject?action_code=bmhnvz`. */
export interface PdfFileType {
  ID: string;
  NAME: string;
}

export interface UploadPdfResponse {
  callSuccess?: string;
  errorMessage?: string;
  // The backend may attach additional fields — `any` is tolerated downstream.
  [key: string]: any;
}

export interface GetPdfFileNameResponse {
  callSuccess?: string;
  errorMessage?: string;
  fileName?: string;
}

/**
 * HTTP port of the four PDF methods on `EfileFactory` plus the two
 * `loadJsonObject` action calls used by the legacy `PDFUploadController`.
 *
 * Legacy sources:
 *   - ui/app/components/efile/EfileService.js L341-482 (viewPdf / deletePdf /
 *     getPdfFileName / uploadPdf against `GENERAL_CONFIG.gtw_efile_api_url`).
 *   - ui/app/components/efile/efileElements/EfileElementsController.js
 *     L1919-2116 (PDFUploadController) — the two `loadJsonObject` calls at
 *     L2094 (fileTypeList) and L2105 (uploaded PDFs grid) go against the
 *     plain `GENERAL_CONFIG.base_url`.
 *
 * NOTE: Two different base URLs are in play here:
 *   - `baseUrls.efile` (`/gtw-efile-api`) for the four PDF endpoints
 *   - `baseUrls.api`   (`/gtw`)           for `loadJsonObject` lookups
 */
@Injectable({ providedIn: 'root' })
export class PdfUploadService {
  private static readonly ACTION_CODES = {
    fileTypes: 'bmhnvz',
    uploadedPdfs: 'bma33d',
  } as const;

  constructor(private http: HttpClient) {}

  // ---------------------------------------------------------------------------
  // loadJsonObject lookups (baseUrls.api, i.e. /gtw)
  // ---------------------------------------------------------------------------

  loadFileTypes(
    baseUrls: GtwElementsBaseUrlsModel,
    ssoId: string,
  ): Observable<{ callSuccess?: string; jsonObject?: PdfFileType[] }> {
    const params = new HttpParams({
      fromObject: {
        action_code: PdfUploadService.ACTION_CODES.fileTypes,
        sso_id: ssoId ?? '',
      },
    });
    return this.http.get<{ callSuccess?: string; jsonObject?: PdfFileType[] }>(
      `${baseUrls.api}/loadJsonObject`,
      { params, withCredentials: true },
    );
  }

  loadUploadedPdfs(
    baseUrls: GtwElementsBaseUrlsModel,
    submissionId: string | number,
  ): Observable<{ callSuccess?: string; jsonObject?: UploadedPdf[] }> {
    const params = new HttpParams({
      fromObject: {
        action_code: PdfUploadService.ACTION_CODES.uploadedPdfs,
        submission_id: String(submissionId ?? ''),
      },
    });
    return this.http.get<{ callSuccess?: string; jsonObject?: UploadedPdf[] }>(
      `${baseUrls.api}/loadJsonObject`,
      { params, withCredentials: true },
    );
  }

  // ---------------------------------------------------------------------------
  // efile API (baseUrls.efile, i.e. /gtw-efile-api)
  // ---------------------------------------------------------------------------

  /** Mirrors EfileFactory.uploadPdf (EfileService.js L464-L483). */
  uploadPdf(
    baseUrls: GtwElementsBaseUrlsModel,
    submissionId: string | number,
    fileType: string,
    fileName: string,
    comments: string,
    file: File,
    isSignatureDocument: boolean,
  ): Observable<UploadPdfResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new HttpParams({
      fromObject: {
        submissionId: String(submissionId ?? ''),
        fileType: fileType ?? '',
        fileName: fileName ?? '',
        comments: comments ?? '',
        isSignatureDocument: isSignatureDocument ? 'Y' : 'N',
      },
    });

    return this.http.post<UploadPdfResponse>(
      `${baseUrls.efile}/uploadPdf`,
      formData,
      { params, withCredentials: true },
    );
  }

  /** Mirrors EfileFactory.viewPdf (EfileService.js L341-L348). Returns the
   * raw PDF bytes so callers can `new Blob([res], { type: 'application/pdf' })`
   * and open it in a new tab. */
  viewPdf(
    baseUrls: GtwElementsBaseUrlsModel,
    submissionId: string | number,
    fileName: string,
  ): Observable<ArrayBuffer> {
    const params = new HttpParams({
      fromObject: {
        submissionId: String(submissionId ?? ''),
        fileName: fileName ?? '',
      },
    });
    return this.http.post(`${baseUrls.efile}/viewPdf`, null, {
      params,
      responseType: 'arraybuffer',
      withCredentials: true,
    });
  }

  /** Mirrors EfileFactory.deletePdf (EfileService.js L349-L356). */
  deletePdf(
    baseUrls: GtwElementsBaseUrlsModel,
    submissionId: string | number,
    fileName: string,
  ): Observable<UploadPdfResponse> {
    const params = new HttpParams({
      fromObject: {
        submissionId: String(submissionId ?? ''),
        fileName: fileName ?? '',
      },
    });
    return this.http.post<UploadPdfResponse>(`${baseUrls.efile}/deletePdf`, null, {
      params,
      withCredentials: true,
    });
  }

  /** Mirrors EfileFactory.getPdfFileName (EfileService.js L357-L364). */
  getPdfFileName(
    baseUrls: GtwElementsBaseUrlsModel,
    submissionId: string | number,
    fileType: string,
  ): Observable<GetPdfFileNameResponse> {
    const params = new HttpParams({
      fromObject: {
        submissionId: String(submissionId ?? ''),
        fileType: fileType ?? '',
      },
    });
    return this.http.post<GetPdfFileNameResponse>(
      `${baseUrls.efile}/getPdfFileName`,
      null,
      { params, withCredentials: true },
    );
  }
}
